import { fillingStatusFromValue } from '../../utils/fillingType';
import { parseNumberOrEmptyString } from '../../admin/inventories/utils';
import { getDefaultFormValues as getBarrelDefaultFormValues } from '../barrel/getDefaultFormValues';
import { getDefaultFormValues as getTruncatedConeDefaultFormValues } from '../truncatedCone/getDefaultFormValues';
import { getDefaultFormValues as getOtherContainerDefaultFormValues } from '../otherContainer/getDefaultFormValues';
import { getDefaultFormValues as getWeighedDefaultFormValues } from '../weighedContainer/getDefaultFormValues';

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);

/**
 * Utility functions for operation logic
 */

export const getOldFormValues = (
  containerType,
  t,
  currentContainerData,
  mappingConfig,
  globalState,
) => {
  if (containerType === 'truncated-cone') {
    return getTruncatedConeDefaultFormValues(
      t,
      currentContainerData,
      mappingConfig.columns,
      globalState,
      null, // No previous values needed for old values
      fillingStatusFromValue,
      mappingConfig.customFields,
    );
  }

  if (containerType === 'standing' || containerType === 'laying') {
    return getBarrelDefaultFormValues(
      t,
      currentContainerData,
      mappingConfig.columns,
      globalState,
      null, // No previous values needed for old values
      fillingStatusFromValue,
      mappingConfig.customFields,
    );
  }

  if (containerType === 'weighed-container') {
    return getWeighedDefaultFormValues(
      currentContainerData,
      mappingConfig.columns,
      mappingConfig.customFields,
    );
  }

  return getOtherContainerDefaultFormValues(
    t,
    currentContainerData,
    mappingConfig.columns,
    globalState,
    null, // No previous values needed for old values
    fillingStatusFromValue,
    mappingConfig.customFields,
  );
};

export const determineNewInventoryStatus = (
  currentStatus,
  progressABVPercentage,
  progressVolumePercentage,
) => {
  let newStatus = currentStatus;
  if (newStatus === 'initial') {
    newStatus = 'inProgress';
  }
  if (newStatus === 'inProgress' && progressABVPercentage === 100 && progressVolumePercentage === 100) {
    newStatus = 'readyForReview';
  }
  if (newStatus === 'readyForReview' && (progressABVPercentage < 100 || progressVolumePercentage < 100)) {
    newStatus = 'inProgress';
  }
  return newStatus;
};

export const calculateProgressPercentageABV = (containersArray) => {
  const totalItems = containersArray.length;
  const completedItems = containersArray.filter((container) => container.abvStatus === 'done').length;
  return (completedItems / totalItems) * 100;
};

export const calculateProgressPercentageVolume = (containersArray) => {
  const totalItems = containersArray.length;
  const completedItems = containersArray.filter((container) => container.volumeStatus === 'done').length;
  return (completedItems / totalItems) * 100;
};

// Match the backend's completion rules using the final mapped row, including
// filling changes made offline before any server acknowledgement arrives.
export const getMappedContainerCompletion = (container, mappingConfig) => {
  const getVal = (key) => {
    const config = mappingConfig.columns[key];
    return config?.enabled && container[config.mapping?.csvName];
  };
  const rawFilling = getVal('filling');
  const filling = fillingStatusFromValue(typeof rawFilling === 'string' ? rawFilling.trim() : rawFilling);
  if (!filling) return {};
  const empty = filling === 'empty';
  const hasMass = !!getVal('mass') && !!getVal('containerMass');
  const hasVolume = !!getVal('liquidHeight') || !!getVal('emptyHeight') || !!getVal('liquidVolume');
  const abvDone = empty || !!getVal('volumetricMass') || (!!getVal('tav') && !!getVal('tavTemperature'));
  const volumeDone = empty || hasMass || (!!getVal('volumeTemperature') && (filling === 'full' || hasVolume));
  return { abvStatus: abvDone ? 'done' : 'toDo', volumeStatus: volumeDone ? 'done' : 'toDo' };
};

export const getNewVolumeStatusDone = (fillingStatus, containerType, values) => {
  if (fillingStatus === 'full') {
    if (containerType === 'weighed-container') {
      return !!(values?.mass?.value) && !!(values?.containerMass?.value);
    }
    return !!values?.volumeTemp?.value;
  }
  if (fillingStatus === 'empty') {
    return true;
  }
  if (containerType === 'weighed-container') {
    return !!(values?.mass?.value) && !!(values?.containerMass?.value);
  }
  const hasVolumeTemperature = !!values?.volumeTemp?.value;
  const hasVolumeMeasurement = !!values?.liquidHeight?.value
    || !!values?.emptyHeight?.value || !!values?.liquidVolume?.value;
  return hasVolumeTemperature && hasVolumeMeasurement;
};

export const getNewABVStatusDone = (fillingStatus, containerType, values) => {
  if (fillingStatus === 'empty') {
    return true;
  }
  if (containerType === 'weighed-container') {
    // realTAV optional; volumetricMass required
    return !!(values?.volumetricMass?.value);
  }
  // New simplified logic: tavStatus is done if tavTemperature and tav are filled
  const hasTavTemperature = !!values?.tavTemp?.value;
  const hasTav = !!values?.tav?.value;
  return hasTavTemperature && hasTav;
};

export const buildMappedUpdates = (formValues, mappingConfig) => {
  const pairs = [
    ['totalCapacity', formValues?.totalCapacity?.value ?? ''],
    ['usableCapacity', formValues?.usableCapacity?.value ?? ''],
    ['heightUnderBung', formValues?.diameterBond?.value ?? ''],
    ['internalDiameterDown', formValues?.diameterDown?.value ?? ''],
    ['internalDiameterUp', formValues?.diameterUp?.value ?? ''],
    ['liquidHeight', formValues?.liquidHeight?.value ?? ''],
    ['emptyHeight', formValues?.emptyHeight?.value ?? ''],
    ['liquidVolume', formValues?.liquidVolume?.value ?? ''],
    ['tav', formValues?.tav?.value ?? ''],
    ['tavTemperature', formValues?.tavTemp?.value ?? ''],
    ['volumeTemperature', formValues?.volumeTemp?.value ?? ''],
  ];

  const mappedUpdates = pairs.reduce((acc, [mappingKey, rawValue]) => {
    const mapping = mappingConfig.columns?.[mappingKey];
    if (mapping?.enabled && mapping?.mapping?.csvName) {
      acc[mapping.mapping.csvName] = parseNumberOrEmptyString(rawValue);
    }
    return acc;
  }, {});

  // Add custom operator field updates using unified form values
  if (mappingConfig.customFields && mappingConfig.columns) {
    // Import conversion function dynamically to avoid circular dependencies
    // eslint-disable-next-line global-require
    const { convertValueForStorage, getCustomFieldType } = require(
      'components/admin/inventory/mapping/customFieldUtils',
    );

    mappingConfig.customFields.forEach((fieldId) => {
      const fieldConfig = mappingConfig.columns[fieldId];
      const formFieldValue = formValues?.[fieldId];
      if (fieldConfig?.enabled && fieldConfig?.mapping?.csvName
        && formFieldValue && hasOwn(formFieldValue, 'value')
        && formFieldValue.value !== undefined && formFieldValue.value !== null) {
        const { type, format } = getCustomFieldType(fieldConfig);
        // Convert value according to type/format before storage
        const convertedValue = convertValueForStorage(formFieldValue.value, type, format);
        mappedUpdates[fieldConfig.mapping.csvName] = convertedValue;
      }
    });
  }

  return mappedUpdates;
};

export const updateContainerWithMappedValues = (container, values, mappingConfig, containerType) => {
  const base = {
    ...container,
    ...buildMappedUpdates(values, mappingConfig),
  };

  if (containerType === 'weighed-container') {
    if (mappingConfig.columns.mass.mapping.csvName) {
      base[mappingConfig.columns.mass.mapping.csvName] = values.mass?.value ?? '';
    }
    if (mappingConfig.columns.containerMass.mapping.csvName) {
      const containerMassCsv = mappingConfig.columns.containerMass.mapping.csvName;
      base[containerMassCsv] = values.containerMass?.value ?? '';
    }
    if (mappingConfig.columns.volumetricMass.mapping.csvName) {
      const volumetricMassCsv = mappingConfig.columns.volumetricMass.mapping.csvName;
      base[volumetricMassCsv] = values.volumetricMass?.value ?? '';
    }
    if (mappingConfig.columns.realTAV.mapping.csvName) {
      base[mappingConfig.columns.realTAV.mapping.csvName] = values.realTAV?.value ?? '';
    }
  }

  return base;
};
